import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import {
  ArrowUpRight,
  Boxes,
  Briefcase,
  CheckCircle2,
  Compass,
  Fingerprint,
  GraduationCap,
  Layers,
  LineChart,
  Rocket,
  Sparkles,
  TrendingUp,
  UserCircle2,
  Users,
  Heart,
  Globe,
  ShieldCheck,
  Mail,
} from "lucide-react";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import SeoHead from "../components/SeoHead";
import { useAppNavigation } from "../hooks/useAppNavigation";
import { useSiteSettings } from "../hooks/useSiteSettings";
import { ROLES } from "../context/AuthContext";

/**
 * Public Landing page — marketing only.
 * No technical/developer sections here. Those live under /admin (protected).
 */
export default function PublicLanding() {
  const { comingSoon, goToRoleSpace } = useAppNavigation();
  const location = useLocation();
  const { t } = useTranslation();
  const { settings } = useSiteSettings();

  // CMS overlays — fallback vers i18n si aucune valeur configurée en admin.
  const cms = settings || {};
  const cmsHero = cms.hero || {};
  const cmsStats = cms.stats || {};
  const cmsMission = cms.mission || {};
  const cmsSections = cms.sections || {};
  const showSection = (key) => cmsSections?.[key]?.visible !== false;
  const val = (v, fallback) => (v && String(v).trim() ? v : fallback);

  // Smooth scroll when navigating with hash (e.g., #profils, #ecosystemes, #faq).
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Wait a tick for the DOM to render sections
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, [location.hash]);

  return (
    <div className="afr-root" data-testid="afr-public-root">
      <SeoHead
        title="Écosystème digital panafricain"
        description="AFRICTAL connecte talents, freelances, entreprises, entrepreneurs, universités et investisseurs africains dans un écosystème digital certifié, transparent et souverain."
        path="/"
        keywords="AFRICTAL, écosystème panafricain, talents Afrique, freelance Afrique, emploi, entrepreneurs, investisseurs, formation, certification"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "AFRICTAL",
          url: (typeof window !== "undefined" ? window.location.origin : ""),
          logo: (typeof window !== "undefined" ? window.location.origin : "") + "/brand/africtal-logo.png",
          sameAs: [],
          description: "Plateforme panafricaine réunissant talents, freelances, entreprises, entrepreneurs, universités et investisseurs.",
        }}
      />
      <PublicNav />

      {/* HERO — écosystème panafricain immersif */}
      <header className="afr-hero afr-hero--split" data-testid="afr-hero">
        <div className="afr-hero__grid">
          {/* PARTIE GAUCHE — récit + CTAs + trust bar */}
          <div className="afr-hero__left">
            <div className="afr-hero__meta">
              <span className="afr-eyebrow" data-testid="hero-eyebrow">
                {val(cmsHero.eyebrow, t("landing.hero.eyebrow"))}
              </span>
            </div>
            <h1 className="afr-hero__title" id="accueil">
              <em>{val(cmsHero.title_1, t("landing.hero.title_1"))}</em> <br />
              {val(cmsHero.title_2, t("landing.hero.title_2"))} <br />
              <em>{val(cmsHero.title_3, t("landing.hero.title_3"))}</em>
            </h1>
            <p className="afr-hero__lead">
              {val(cmsHero.lead, t("landing.hero.lead"))}
            </p>

            <div className="afr-hero__ctas afr-flex-row" data-testid="hero-ctas">
              <Link
                to="/login?mode=signup"
                className="afr-btn afr-btn--primary afr-btn--lg"
                data-testid="hero-btn-signup"
              >
                <Rocket size={16} /> {val(cmsHero.cta_signup_label, t("landing.hero.cta_signup"))}
                <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/login"
                className="afr-btn afr-btn--outline afr-btn--lg"
                data-testid="hero-btn-discover"
              >
                {val(cmsHero.cta_login_label, t("landing.hero.cta_login"))}
              </Link>
            </div>

            <ul className="afr-hero__trust" data-testid="hero-trust-bar">
              <li><ShieldCheck size={14} /> Données sécurisées</li>
              <li><Fingerprint size={14} /> Passeport numérique vérifié</li>
              <li><Globe size={14} /> Plateforme panafricaine</li>
              <li><Sparkles size={14} /> Accompagnement personnalisé</li>
              <li><CheckCircle2 size={14} /> Disponible 24/7</li>
            </ul>

            <div className="afr-hero__stats" data-testid="afr-hero-stats">
              <div className="afr-stat">
                <span className="afr-stat__label">Talents connectés</span>
                <span className="afr-stat__value afr-stat__value--ok">
                  <CheckCircle2 size={22} /> {val(cmsStats.talents, "28k+")}
                </span>
              </div>
              <div className="afr-stat">
                <span className="afr-stat__label">Opportunités actives</span>
                <span className="afr-stat__value">{val(cmsStats.offers, "1 240+")}</span>
              </div>
              <div className="afr-stat">
                <span className="afr-stat__label">Coachs spécialisés</span>
                <span className="afr-stat__value">9</span>
              </div>
              <div className="afr-stat">
                <span className="afr-stat__label">Pays / devises</span>
                <span className="afr-stat__value">{val(cmsStats.countries, "27")}</span>
              </div>
            </div>
          </div>

          {/* PARTIE DROITE — image CMS OU composition écosystème par défaut */}
          <div className="afr-hero__right" aria-hidden="true" data-testid="hero-composition">
            {cmsHero.banner_image_url ? (
              <div className="afr-hero-scene" data-testid="hero-banner-cms" style={{ borderRadius: 24, overflow: "hidden", boxShadow: "0 24px 60px rgba(15,42,66,0.18)" }}>
                <img
                  src={cmsHero.banner_image_url}
                  alt="Bannière AFRICTAL"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
            ) : (
            <div className="afr-hero-scene">
              {/* Halo lumineux */}
              <div className="afr-hero-scene__halo" />
              <div className="afr-hero-scene__halo afr-hero-scene__halo--gold" />

              {/* Carte de l'Afrique stylisée + réseau */}
              <svg
                className="afr-hero-scene__map"
                viewBox="0 0 520 560"
                fill="none"
                role="img"
                aria-label="Carte stylisée de l'Afrique connectée"
              >
                <defs>
                  <linearGradient id="afrMapGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="var(--afr-primary)" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="var(--afr-accent)" stopOpacity="0.6" />
                  </linearGradient>
                  <radialGradient id="afrNode" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="var(--afr-accent)" stopOpacity="1" />
                    <stop offset="60%" stopColor="var(--afr-primary)" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="var(--afr-primary)" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* Silhouette Afrique (stylisée) */}
                <path
                  d="M255 40 C305 40 340 70 355 110 C385 120 405 155 400 195 C420 220 425 265 405 300 C420 335 405 380 385 405 C395 440 375 480 340 495 C325 525 285 540 255 530 C230 545 195 535 175 510 C145 505 120 475 120 440 C95 420 80 380 95 345 C80 315 90 270 115 245 C100 220 110 180 140 165 C145 130 175 100 210 90 C215 60 235 45 255 40 Z"
                  fill="url(#afrMapGrad)"
                  fillOpacity="0.12"
                  stroke="url(#afrMapGrad)"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />

                {/* Grille de fond légère (grands cercles) */}
                <g opacity="0.15" stroke="var(--afr-primary)" strokeDasharray="2 6">
                  <circle cx="260" cy="280" r="220" />
                  <circle cx="260" cy="280" r="160" />
                  <circle cx="260" cy="280" r="100" />
                </g>

                {/* Lignes de connexion réseau */}
                <g stroke="var(--afr-primary)" strokeWidth="1" opacity="0.55">
                  <line x1="170" y1="140" x2="330" y2="220" className="afr-hero-scene__line" />
                  <line x1="330" y1="220" x2="200" y2="360" className="afr-hero-scene__line afr-hero-scene__line--d1" />
                  <line x1="200" y1="360" x2="360" y2="420" className="afr-hero-scene__line afr-hero-scene__line--d2" />
                  <line x1="360" y1="420" x2="240" y2="480" className="afr-hero-scene__line afr-hero-scene__line--d3" />
                  <line x1="170" y1="140" x2="240" y2="480" className="afr-hero-scene__line afr-hero-scene__line--d4" />
                  <line x1="330" y1="220" x2="360" y2="420" className="afr-hero-scene__line afr-hero-scene__line--d5" />
                </g>

                {/* Halos autour des nœuds */}
                <g>
                  <circle cx="170" cy="140" r="30" fill="url(#afrNode)" opacity="0.7" />
                  <circle cx="330" cy="220" r="30" fill="url(#afrNode)" opacity="0.7" />
                  <circle cx="200" cy="360" r="30" fill="url(#afrNode)" opacity="0.7" />
                  <circle cx="360" cy="420" r="30" fill="url(#afrNode)" opacity="0.7" />
                  <circle cx="240" cy="480" r="30" fill="url(#afrNode)" opacity="0.7" />
                  <circle cx="150" cy="270" r="20" fill="url(#afrNode)" opacity="0.5" />
                  <circle cx="400" cy="310" r="20" fill="url(#afrNode)" opacity="0.5" />
                </g>

                {/* Nœuds pulsants */}
                <g>
                  <circle className="afr-hero-scene__node" cx="170" cy="140" r="5" />
                  <circle className="afr-hero-scene__node afr-hero-scene__node--d1" cx="330" cy="220" r="5" />
                  <circle className="afr-hero-scene__node afr-hero-scene__node--d2" cx="200" cy="360" r="5" />
                  <circle className="afr-hero-scene__node afr-hero-scene__node--d3" cx="360" cy="420" r="5" />
                  <circle className="afr-hero-scene__node afr-hero-scene__node--d4" cx="240" cy="480" r="5" />
                  <circle className="afr-hero-scene__node afr-hero-scene__node--d5" cx="150" cy="270" r="4" />
                  <circle className="afr-hero-scene__node afr-hero-scene__node--d6" cx="400" cy="310" r="4" />
                </g>
              </svg>

              {/* Personas — 6 talents africains identifiés par métier + région */}
              <div className="afr-hero-scene__personas" data-testid="hero-personas">
                {[
                  { initials: "AB", label: "Étudiant",       region: "Sénégal",         action: "Nouvelle opportunité",  photo: "https://images.unsplash.com/photo-1508243529287-e21914733111?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-formation), var(--afr-accent))",      x: "8%",  y: "18%", delay: "0s"   },
                  { initials: "KD", label: "Développeuse",   region: "Nigeria",         action: "Mission freelance",     photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-freelance), var(--afr-accent))",     x: "56%", y: "8%",  delay: "0.3s" },
                  { initials: "MO", label: "Ingénieur",      region: "Kenya",           action: "Certification vérifiée", photo: "https://images.unsplash.com/photo-1531384441138-2736e62e0919?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-talent), var(--afr-accent))",        x: "78%", y: "34%", delay: "0.6s" },
                  { initials: "FN", label: "Entrepreneure",  region: "Côte d'Ivoire",   action: "Projet financé",         photo: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-entrepreneur), var(--afr-accent))",  x: "10%", y: "62%", delay: "0.9s" },
                  { initials: "SN", label: "Employeur",      region: "Rwanda",          action: "Recrutement en cours",   photo: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-emploi), var(--afr-accent))",        x: "70%", y: "72%", delay: "1.2s" },
                  { initials: "TW", label: "Artisane",       region: "Maroc",           action: "Compétence validée",     photo: "https://images.unsplash.com/photo-1601288496920-b6154fe3626a?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-investissement), var(--afr-primary))", x: "40%", y: "86%", delay: "1.5s" },
                ].map((p) => (
                  <div
                    key={p.label}
                    className="afr-hero-persona"
                    style={{ left: p.x, top: p.y, animationDelay: p.delay }}
                    data-testid={`hero-persona-${p.label.toLowerCase()}`}
                  >
                    <div
                      className="afr-hero-persona__avatar afr-hero-persona__avatar--photo"
                      style={{ background: p.gradient, backgroundImage: `url(${p.photo}), ${p.gradient}`, backgroundSize: "cover", backgroundPosition: "center" }}
                    >
                      <span className="afr-hero-persona__initials" aria-hidden="true">{p.initials}</span>
                    </div>
                    <div className="afr-hero-persona__meta">
                      <span className="afr-hero-persona__label">{p.label}</span>
                      <span className="afr-hero-persona__region">🌍 {p.region}</span>
                      <span className="afr-hero-persona__action">{p.action}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Cartes flottantes — services AFRICTAL */}
              <div className="afr-hero-scene__cards" data-testid="hero-floating-cards">
                <div className="afr-hero-card afr-hero-card--tl">
                  <div className="afr-hero-card__icon" style={{ color: "var(--mod-emploi)" }}><Briefcase size={16} /></div>
                  <div>
                    <div className="afr-hero-card__title">Offres d'emploi</div>
                    <div className="afr-hero-card__value">1 240 <span className="afr-hero-card__delta">actives</span></div>
                  </div>
                </div>

                <div className="afr-hero-card afr-hero-card--tr">
                  <div className="afr-hero-card__icon" style={{ color: "var(--mod-talent)" }}><Users size={16} /></div>
                  <div>
                    <div className="afr-hero-card__title">Talents actifs</div>
                    <div className="afr-hero-card__value">28k+ <span className="afr-hero-card__delta">panafricains</span></div>
                  </div>
                </div>

                <div className="afr-hero-card afr-hero-card--ml">
                  <div className="afr-hero-card__icon" style={{ color: "var(--mod-freelance)" }}><Compass size={16} /></div>
                  <div>
                    <div className="afr-hero-card__title">Freelances</div>
                    <div className="afr-hero-card__value">4 800 <span className="afr-hero-card__delta">disponibles</span></div>
                  </div>
                </div>

                <div className="afr-hero-card afr-hero-card--mr">
                  <div className="afr-hero-card__icon" style={{ color: "var(--mod-coach-ai)" }}><Sparkles size={16} /></div>
                  <div>
                    <div className="afr-hero-card__title">Accompagnement</div>
                    <div className="afr-hero-card__value">9 <span className="afr-hero-card__delta">coachs experts</span></div>
                  </div>
                </div>

                <div className="afr-hero-card afr-hero-card--bl">
                  <div className="afr-hero-card__icon" style={{ color: "var(--mod-formation)" }}><GraduationCap size={16} /></div>
                  <div>
                    <div className="afr-hero-card__title">Certifications</div>
                    <div className="afr-hero-card__value">342 <span className="afr-hero-card__delta">émises / mois</span></div>
                  </div>
                </div>

                <div className="afr-hero-card afr-hero-card--br">
                  <div className="afr-hero-card__icon" style={{ color: "var(--mod-investissement)" }}><LineChart size={16} /></div>
                  <div>
                    <div className="afr-hero-card__title">Projets financés</div>
                    <div className="afr-hero-card__value">12 M<span className="afr-hero-card__delta"> USD</span></div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </header>

      {/* PROFILS + ÉCOSYSTÈMES sections retirées de la landing.
          Ces contenus vivent désormais dans /solutions pour une lecture unifiée. */}


      {/* À PROPOS / NOTRE MISSION */}
      {showSection("mission") && (
      <section id="mission" className="afr-section" data-testid="afr-mission">
        <div
          className="afr-grid"
          style={{ gridTemplateColumns: "1fr 1.2fr", alignItems: "center", gap: "3rem" }}
        >
          <div>
            <span className="afr-eyebrow">Notre Mission</span>
            <h2 className="afr-section__title" style={{ marginTop: "0.6rem" }}>
              {cmsMission.title && String(cmsMission.title).trim() ? (
                cmsMission.title
              ) : (
                <>L'écosystème numérique qui <em>connecte tout le continent</em>.</>
              )}
            </h2>
            <p className="afr-section__lead" style={{ textAlign: "left", margin: "1rem 0" }}>
              {cmsMission.lead && String(cmsMission.lead).trim() ? (
                cmsMission.lead
              ) : (
                "AFRICTAL n'est pas une simple plateforme de recrutement ni une marketplace freelance. C'est un écosystème complet qui articule les huit maillons du développement du talent africain — de la formation à l'entrepreneuriat, du freelance à l'investissement — en un seul environnement de confiance, panafricain et souverain."
              )}
            </p>
            <p className="afr-section__lead" style={{ textAlign: "left", margin: "1rem 0" }}>
              Notre ambition&nbsp;: donner à chaque acteur africain — talent, entreprise,
              université, entrepreneur, investisseur, coach, mentor ou institution — les outils,
              les opportunités et la visibilité pour bâtir la prochaine décennie du continent.
            </p>
            <div
              className="afr-flex-row"
              style={{ marginTop: "1.4rem", flexWrap: "wrap", gap: "1rem" }}
            >
              <div className="afr-flex-row" style={{ gap: "0.5rem" }}>
                <Heart size={18} style={{ color: "var(--afr-primary)" }} />
                <strong style={{ color: "var(--afr-secondary)" }}>Souveraineté</strong>
              </div>
              <div className="afr-flex-row" style={{ gap: "0.5rem" }}>
                <Globe size={18} style={{ color: "var(--afr-primary)" }} />
                <strong style={{ color: "var(--afr-secondary)" }}>Panafricanité</strong>
              </div>
              <div className="afr-flex-row" style={{ gap: "0.5rem" }}>
                <ShieldCheck size={18} style={{ color: "var(--afr-primary)" }} />
                <strong style={{ color: "var(--afr-secondary)" }}>Confiance</strong>
              </div>
              <div className="afr-flex-row" style={{ gap: "0.5rem" }}>
                <Sparkles size={18} style={{ color: "var(--afr-primary)" }} />
                <strong style={{ color: "var(--afr-secondary)" }}>Excellence</strong>
              </div>
            </div>
            <div className="afr-flex-row" style={{ marginTop: "1.6rem" }}>
              <Link
                to="/a-propos"
                className="afr-btn afr-btn--primary"
                data-testid="mission-btn-learn-more"
              >
                En savoir plus <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/login?mode=signup"
                className="afr-btn afr-btn--outline"
                data-testid="mission-btn-join"
              >
                Rejoindre AFRICTAL
              </Link>
            </div>
          </div>

          <div
            className="afr-card"
            style={{
              padding: "2.4rem",
              background:
                "linear-gradient(135deg, color-mix(in oklab, var(--afr-primary) 8%, var(--afr-surface)), var(--afr-surface))",
              borderColor: "color-mix(in oklab, var(--afr-primary) 25%, var(--afr-border))",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                marginBottom: "1.4rem",
              }}
            >
              <span
                className="afr-mark"
                aria-hidden="true"
                style={{ width: 64, height: 64 }}
              >
                <img src="/brand/africtal-logo.png" alt="AFRICTAL" />
              </span>
              <div>
                <strong
                  style={{
                    fontFamily: "'Outfit',sans-serif",
                    fontSize: "1.4rem",
                    color: "var(--afr-secondary)",
                  }}
                >
                  AFRICTAL
                </strong>
                <div className="afr-muted" style={{ fontSize: "0.9rem" }}>
                  Révéler les talents. Créer les opportunités. Bâtir l'Afrique de demain.
                </div>
              </div>
            </div>
            <blockquote
              style={{
                fontFamily: "'Outfit',sans-serif",
                fontSize: "1.1rem",
                lineHeight: "1.6",
                color: "var(--afr-secondary)",
                fontStyle: "italic",
                margin: 0,
                padding: "1rem 1.2rem",
                borderLeft: "3px solid var(--afr-primary)",
                background: "color-mix(in oklab, var(--afr-primary) 4%, transparent)",
              }}
            >
              « Le continent africain compte 1,4 milliard d'habitants dont 60 % ont moins de 25 ans.
              Il a besoin d'une infrastructure numérique à sa mesure — pas d'une simple job board,
              mais d'un véritable écosystème panafricain de la compétence, de l'innovation et de
              la croissance économique. C'est ce que nous bâtissons. »
            </blockquote>
            <p className="afr-muted" style={{ marginTop: "1rem", fontSize: "0.9rem" }}>
              — Fondateurs AFRICTAL
            </p>
          </div>
        </div>
      </section>
      )}

      {/* Section "Écosystèmes intégrés" retirée de la landing.
          Contenu déplacé vers /solutions#ecosystemes pour éviter la duplication. */}


      {/* COMMENT ÇA MARCHE */}
      {showSection("comment") && (
      <section id="comment" className="afr-section" data-testid="afr-how">
        <h2 className="afr-section__title">Comment AFRICTAL fonctionne</h2>
        <p className="afr-section__lead">
          Trois étapes simples pour rejoindre l'écosystème panafricain.
        </p>
        <div
          className="afr-grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}
        >
          <article className="afr-card">
            <div className="afr-card__badge">Étape 1</div>
            <h3>
              <UserCircle2
                size={22}
                style={{ verticalAlign: "middle", marginRight: "0.4rem", color: "var(--afr-primary)" }}
              />{" "}
              Créez votre Passeport
            </h3>
            <p className="afr-muted">
              Inscrivez-vous gratuitement · Renseignez compétences, formations, expériences ·
              Votre Passeport Numérique est immédiatement disponible et vérifiable.
            </p>
          </article>
          <article className="afr-card">
            <div className="afr-card__badge">Étape 2</div>
            <h3>
              <Sparkles
                size={22}
                style={{ verticalAlign: "middle", marginRight: "0.4rem", color: "var(--afr-primary)" }}
              />{" "}
              Recevez des recommandations personnalisées
            </h3>
            <p className="afr-muted">
              Vos compétences sont mises en lumière · Vous découvrez des opportunités adaptées
              à votre parcours, à vos ambitions et à votre secteur.
            </p>
          </article>
          <article className="afr-card">
            <div className="afr-card__badge">Étape 3</div>
            <h3>
              <Rocket
                size={22}
                style={{ verticalAlign: "middle", marginRight: "0.4rem", color: "var(--afr-primary)" }}
              />{" "}
              Passez à l'action
            </h3>
            <p className="afr-muted">
              Postulez à des offres · Décrochez des missions freelance · Suivez des formations
              · Levez des fonds · Recrutez · Le tout depuis une seule plateforme.
            </p>
          </article>
        </div>
      </section>
      )}

      {/* ACCOMPAGNEMENT SPÉCIALISÉ */}
      {showSection("coach_ia") && (
      <section id="coach-ia" className="afr-section" data-testid="afr-coach-ia">
        <h2 className="afr-section__title">Un accompagnement dédié à chaque parcours</h2>
        <p className="afr-section__lead">
          Neuf accompagnements spécialisés au <strong>service de votre progression</strong> —
          carrière, entrepreneuriat, formation, freelancing… Chacun propose plans de
          progression, recommandations concrètes et capsules quotidiennes pensées pour
          le contexte africain.
        </p>
        <div
          className="afr-grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" }}
        >
          {[
            { code: "career", name: "Carrière", icon: <Compass size={22} /> },
            { code: "leadership", name: "Leadership", icon: <TrendingUp size={22} /> },
            { code: "motivation", name: "Motivation", icon: <Sparkles size={22} /> },
            { code: "entrepreneur", name: "Entrepreneuriat", icon: <Rocket size={22} /> },
            { code: "innovation", name: "Innovation", icon: <Layers size={22} /> },
            { code: "creativity", name: "Créativité", icon: <Boxes size={22} /> },
            { code: "learning", name: "Formation", icon: <GraduationCap size={22} /> },
            { code: "freelance", name: "Freelance", icon: <Briefcase size={22} /> },
            { code: "business", name: "Business", icon: <LineChart size={22} /> },
          ].map((c) => (
            <button
              type="button"
              key={c.code}
              className="afr-persona"
              data-testid={`afr-coach-${c.code}`}
              onClick={() => goToRoleSpace(ROLES.COACH)}
              aria-label={`Coach ${c.name} — se connecter pour y accéder`}
            >
              <div
                className="afr-persona__icon"
                style={{ background: "linear-gradient(135deg, var(--mod-coach-ai), var(--afr-accent))" }}
              >
                {c.icon}
              </div>
              <h3>Coach {c.name}</h3>
              <p>Plans personnalisés · recommandations · capsules quotidiennes.</p>
            </button>
          ))}
        </div>
      </section>
      )}
      {showSection("passeport") && (
      <section id="passeport" className="afr-section" data-testid="afr-passeport">
        <div
          className="afr-grid"
          style={{ gridTemplateColumns: "1.2fr 1fr", alignItems: "center", gap: "3rem" }}
        >
          <div>
            <span className="afr-eyebrow">Le Passeport Numérique AFRICTAL</span>
            <h2 className="afr-section__title" style={{ marginTop: "0.6rem" }}>
              Votre identité professionnelle <em>à vie</em>, portable et souveraine.
            </h2>
            <ul className="afr-list" style={{ marginTop: "1.2rem" }}>
              <li>
                <Fingerprint size={16} style={{ verticalAlign: "middle", marginRight: "0.4rem", color: "var(--afr-primary)" }} />
                Compétences validées, expériences vérifiées, diplômes attestés
              </li>
              <li>
                <ShieldCheck size={16} style={{ verticalAlign: "middle", marginRight: "0.4rem", color: "var(--afr-primary)" }} />
                Signature cryptographique · vérification tierce · audit trail
              </li>
              <li>
                <Globe size={16} style={{ verticalAlign: "middle", marginRight: "0.4rem", color: "var(--afr-primary)" }} />
                Portable entre plateformes · exportable · vous en êtes propriétaire
              </li>
              <li>
                <Sparkles size={16} style={{ verticalAlign: "middle", marginRight: "0.4rem", color: "var(--afr-primary)" }} />
                Un accompagnement personnalisé pour révéler tout votre potentiel
              </li>
            </ul>
            <div className="afr-flex-row" style={{ marginTop: "1.6rem" }}>
              <Link
                to="/login?mode=signup"
                className="afr-btn afr-btn--primary"
                data-testid="passeport-btn-create"
              >
                <Rocket size={16} /> Créer mon Passeport
              </Link>
            </div>
          </div>
          <button
            type="button"
            className="afr-talent-card afr-clickable"
            data-testid="afr-passeport-preview"
            onClick={() => goToRoleSpace(ROLES.TALENT)}
            aria-label="Voir un exemple de Passeport et créer le vôtre"
            style={{
              background: "var(--afr-surface)",
              textAlign: "left",
              font: "inherit",
              color: "inherit",
              border: "1px solid var(--afr-border)",
            }}
          >
            <div className="afr-talent-card__head">
              <div className="afr-talent-card__avatar">KD</div>
              <div>
                <div className="afr-talent-card__name">Kadidiatou Diallo</div>
                <div className="afr-talent-card__title">
                  Full-Stack Engineer · Dakar
                </div>
              </div>
            </div>
            <div>
              <span className="afr-tag-chip">Java</span>
              <span className="afr-tag-chip">Spring Boot</span>
              <span className="afr-tag-chip">React</span>
              <span className="afr-tag-chip">Kafka</span>
            </div>
            <div className="afr-talent-card__stats">
              <div className="afr-talent-card__stat">
                <strong>92%</strong>Passeport
              </div>
              <div className="afr-talent-card__stat">
                <strong>4.9</strong>Réputation
              </div>
              <div className="afr-talent-card__stat">
                <strong>7</strong>Certifications
              </div>
            </div>
          </button>
        </div>
      </section>
      )}

      {/* PARTENAIRES */}
      {showSection("partenaires") && (
      <section id="partenaires" className="afr-section" data-testid="afr-partenaires">
        <h2 className="afr-section__title">Un réseau panafricain de confiance</h2>
        <p className="afr-section__lead">
          AFRICTAL s'appuie sur des partenaires stratégiques dans 27 pays du continent.
        </p>
        <div
          className="afr-grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}
        >
          {[
            "Sonatel","MTN Group","ECOBANK","Safaricom","Orange","Wave",
            "Africa50","AfDB","UNESCO","African Union","AfCFTA","Smart Africa",
          ].map((p) => (
            <button
              type="button"
              key={p}
              className="afr-card"
              data-testid={`partner-${p.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => comingSoon(`Page partenaire ${p}`)}
              aria-label={`Découvrir le partenaire ${p} (en préparation)`}
              style={{ textAlign: "center", padding: "1.2rem" }}
            >
              <strong
                style={{
                  fontFamily: "'Outfit',sans-serif",
                  fontSize: "1.05rem",
                  color: "var(--afr-secondary)",
                  letterSpacing: "-0.01em",
                }}
              >
                {p}
              </strong>
              <p
                className="afr-muted"
                style={{ fontSize: "0.78rem", margin: "0.4rem 0 0" }}
              >
                Partenaire officiel
              </p>
            </button>
          ))}
        </div>
      </section>
      )}

      {/* TÉMOIGNAGES */}
      {showSection("temoignages") && (
      <section id="temoignages" className="afr-section" data-testid="afr-testimonials">
        <h2 className="afr-section__title">Ils font confiance à AFRICTAL</h2>
        <div
          className="afr-grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}
        >
          <article className="afr-testimonial">
            <p>
              AFRICTAL nous a permis de sourcer 3 ingénieurs seniors panafricains en 21
              jours, avec un Passeport vérifiable qui a divisé par 4 notre temps de screening.
            </p>
            <div className="afr-testimonial__author">
              <div className="afr-avatar">SN</div>
              <div>
                <strong>Sokhna N'Diaye</strong>
                <span>VP Talent · Sonatel Group</span>
              </div>
            </div>
          </article>
          <article className="afr-testimonial">
            <p>
              L'accompagnement AFRICTAL est le premier qui comprend vraiment le contexte
              africain de mon parcours. Un game-changer pour la nouvelle génération
              d'entrepreneurs.
            </p>
            <div className="afr-testimonial__author">
              <div
                className="afr-avatar"
                style={{ background: "linear-gradient(135deg, var(--mod-entrepreneur), var(--afr-accent))" }}
              >
                KM
              </div>
              <div>
                <strong>Kwame Mensah</strong>
                <span>Fondateur · Osu Ventures (Accra)</span>
              </div>
            </div>
          </article>
          <article className="afr-testimonial">
            <p>
              Nos étudiants disposent enfin d'un Passeport reconnu au-delà de nos frontières.
              L'intégration avec nos programmes a été rapide et sans friction.
            </p>
            <div className="afr-testimonial__author">
              <div
                className="afr-avatar"
                style={{ background: "linear-gradient(135deg, var(--mod-formation), var(--afr-accent))" }}
              >
                AN
              </div>
              <div>
                <strong>Prof. Aminata Ndiaye</strong>
                <span>Doyenne · Université Cheikh Anta Diop</span>
              </div>
            </div>
          </article>
        </div>
      </section>
      )}

      {/* FAQ */}
      {showSection("faq") && (
      <section id="faq" className="afr-section" data-testid="afr-faq">
        <h2 className="afr-section__title">{t("landing.faq.title")}</h2>
        <p className="afr-section__lead">
          {t("landing.faq.lead_prefix")}{" "}
          <a href="mailto:hello@africtal.com">{t("common.contact_us")}</a>.
        </p>
        <div
          className="afr-grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}
        >
          {["what", "who", "cost", "countries", "privacy", "coach", "integration"].map((k) => (
            <details className="afr-card" key={k} data-testid={`faq-item-${k}`}>
              <summary
                style={{
                  cursor: "pointer",
                  fontFamily: "'Outfit',sans-serif",
                  fontWeight: 600,
                  fontSize: "1.05rem",
                  color: "var(--afr-secondary)",
                }}
              >
                {t(`landing.faq.items.${k}.q`)}
              </summary>
              <p className="afr-muted" style={{ marginTop: "0.7rem" }}>
                <Trans
                  i18nKey={`landing.faq.items.${k}.a`}
                  components={{
                    1: k === "integration"
                      ? <a href="mailto:hello@africtal.com" />
                      : <strong />,
                  }}
                />
              </p>
            </details>
          ))}
        </div>
      </section>
      )}

      {/* CTA final */}
      {showSection("cta") && (
      <section className="afr-section" data-testid="afr-cta">
        <div className="afr-cta-banner" data-testid="afr-cta-banner">
          <div className="afr-cta-banner__text">
            <h2>{t("landing.cta_final.title")}</h2>
            <p>{t("landing.cta_final.lead")}</p>
          </div>
          <div className="afr-cta-banner__actions">
            <Link
              to="/login?mode=signup"
              className="afr-btn afr-btn--primary afr-btn--lg"
              data-testid="cta-btn-primary"
            >
              <Rocket size={16} /> {t("landing.cta_final.primary")}
            </Link>
            <a
              href="mailto:hello@africtal.com"
              className="afr-btn afr-btn--outline afr-btn--lg"
              style={{ color: "#F7F5F0", borderColor: "rgba(247,245,240,0.3)" }}
              data-testid="cta-btn-secondary"
            >
              <Mail size={16} /> {t("landing.cta_final.secondary")}
            </a>
          </div>
        </div>
      </section>
      )}

      <PublicFooter />
    </div>
  );
}
